import React from 'react';
import { PolicyLayout } from '../components/legal/PolicyLayout';

export default function DataProtectionPolicy() {
  return (
    <PolicyLayout title="Data Protection Policy (DPDP)" lastUpdated="20 February 2026">
      <p>This Data Protection Policy describes how <strong>Plus Ultra Innovation</strong> (the "Data Fiduciary"), operating the <strong>ProcureOne Ultra</strong> platform, processes personal data of natural persons ("Data Principals") in compliance with the <strong>Digital Personal Data Protection Act, 2023</strong> ("DPDP Act") and the Information Technology Act, 2000.</p>

      <h2>1. Role</h2>
      <p>Plus Ultra Innovation is the <strong>Data Fiduciary</strong> within the meaning of DPDP §2(i). Razorpay, Resend, and our cloud storage provider act as <strong>Data Processors</strong> under contract.</p>

      <h2>2. Lawful Processing &amp; Consent</h2>
      <p>We process personal data only on lawful grounds:</p>
      <ul>
        <li><strong>Consent</strong> (§6): free, specific, informed and unambiguous, captured at registration and at each payment checkout. We store the timestamp, policy version and IP at the moment of consent.</li>
        <li><strong>Legitimate uses</strong> (§7): performance of the procurement contract, compliance with law, employment-related purposes (for Plus Ultra staff), and to provide any subsidy, benefit, service, certificate or licence by the State.</li>
      </ul>

      <h2>3. Notice Provided at Collection</h2>
      <p>Before collecting your data, we tell you (a) what data is collected, (b) the purpose, (c) how to withdraw consent, (d) how to lodge a grievance, and (e) how to complain to the Data Protection Board of India.</p>

      <h2>4. Data Principal Rights (DPDP §11–§14)</h2>
      <ul>
        <li><strong>Right to information</strong> about processing &mdash; this Policy.</li>
        <li><strong>Right to access &amp; export</strong> &mdash; one-click JSON export at <a href="/privacy" className="underline">/privacy</a> while logged in.</li>
        <li><strong>Right to correction</strong> &mdash; via profile page.</li>
        <li><strong>Right to erasure</strong> &mdash; type <code>DELETE_MY_ACCOUNT</code> at <a href="/privacy" className="underline">/privacy</a>; account erased; all JWTs revoked; operational data deleted within 30 days subject to tax / accounting retention.</li>
        <li><strong>Right to grievance redressal</strong> &mdash; contact the Grievance Officer below; 7-working-day SLA.</li>
        <li><strong>Right to nominate</strong> a person to exercise rights on death / incapacity.</li>
      </ul>

      <h2>5. Children &amp; Persons with Disabilities (§9)</h2>
      <p>We do not knowingly collect data of children below 18. Our platform is intended for businesses and adult representatives only. If we discover such data has been collected, it is erased immediately.</p>

      <h2>6. Reasonable Security Safeguards (§8)</h2>
      <ul>
        <li>bcrypt password hashing; JWT with server-side revocation on logout / reset / delete.</li>
        <li>HMAC-SHA256 webhook signature verification with 5-minute replay window.</li>
        <li>Role-based access control + horizontal-IDOR mitigation on tender reads.</li>
        <li>Upload MIME + extension whitelist (blocks executable types).</li>
        <li>Append-only <code>audit_logs</code> capturing payment lifecycle, auth events, DPDP requests.</li>
        <li>Encryption-at-rest on the database and object storage.</li>
        <li>Quarterly internal security review; annual third-party penetration test (post-launch).</li>
      </ul>

      <h2>7. Personal Data Breach Notification (§8(6))</h2>
      <p>If a personal-data breach occurs, we will notify the Data Protection Board of India and affected Data Principals "in such form and manner as may be prescribed", typically within 72 hours of becoming aware, including remedial steps taken.</p>

      <h2>8. Data Retention</h2>
      <p>Active account: retained for the life of the account.<br/>
      Post-erasure: operational data deleted within 30 days.<br/>
      Tax / GST records: retained as required by the Income Tax Act, 1961 (8 years) and the CGST Act, 2017 (6 years).<br/>
      Audit logs: retained for the longer of (a) 6 years or (b) statutory requirement.</p>

      <h2>9. Cross-Border Transfers (§16)</h2>
      <p>Where a Data Processor operates servers outside India, transfers occur only to jurisdictions notified by the Central Government and under contractual safeguards (standard contractual clauses, equivalent-protection commitments).</p>

      <h2>10. Significant Data Fiduciary Obligations (§10)</h2>
      <p>If notified by the Central Government as a Significant Data Fiduciary, we will appoint a Data Protection Officer based in India, an independent data auditor, and conduct periodic Data Protection Impact Assessments. Current status: <strong>not notified</strong>.</p>

      <h2>11. Grievance Officer (§13)</h2>
      <address className="not-italic">
        <strong>Amrapali Sawant</strong><br/>
        Grievance Officer · Plus Ultra Innovation<br/>
        H. No. 903, Manpada, Near Suresh Kirana Stores, Thane, Maharashtra &mdash; 400607<br/>
        Email: <a href="mailto:sawant.ds@gmail.com" className="underline">sawant.ds@gmail.com</a><br/>
        Response SLA: 7 working days
      </address>

      <h2>12. Right to Approach the Data Protection Board of India (§14)</h2>
      <p>If unsatisfied with our response, you may lodge a complaint with the Data Protection Board of India once it is constituted by the Central Government under §18 of the DPDP Act.</p>

      <h2>13. Updates</h2>
      <p>This Policy will be updated when the law, our processing activities, or our processors change. The version and effective date appear at the top of this page. Users will be re-prompted to consent on material changes.</p>
    </PolicyLayout>
  );
}
